export interface LotOption {
  id: string;
  name: string;
  remaining: number; // This should be the Effective Remaining
}

export interface AllocatedLot {
  lotId: string;
  remaining: number;
  usedWeight: string;
}

/**
 * Lot Allocation Logic
 * 
 * Strategy:
 * 1. Filter available lots (remaining > 0).
 * 2. Sort order is determined by the caller (usually FIFO from API).
 * 3. Iterate through lots and allocate quantity until target weight is met.
 * 4. Handle decimal precision (3 decimal places).
 * 5. Return allocated lots and optional warning if stock is insufficient.
 * 
 * Solves:
 * - Smart Allocation for Work Order edits (considers effective remaining).
 * - Multiple Lots support.
 * - Prevents allocation of empty lots.
 */
export const calculateAllocation = (
  targetWeight: number,
  availableLots: LotOption[]
): { lots: AllocatedLot[]; warning?: string } => {
  if (targetWeight <= 0) {
    return { lots: [], warning: 'Target weight must be greater than 0.' };
  }

  let remainingTarget = targetWeight;
  const newLots: AllocatedLot[] = [];
  
  // Filter lots with remaining > 0
  const validLots = availableLots.filter(l => l.remaining > 0);

  for (const lot of validLots) {
      if (remainingTarget <= 0.0001) break; // Tolerance
      
      const quantityToTake = Math.min(lot.remaining, remainingTarget);
      
      // Fix floating point precision for subtraction
      // Use 3 decimal places for consistency
      const preciseQty = Number(quantityToTake.toFixed(3));
      
      newLots.push({
          lotId: lot.id,
          remaining: lot.remaining,
          usedWeight: preciseQty.toString()
      });
      
      remainingTarget -= preciseQty;
  }
  
  if (newLots.length === 0) {
    return { lots: [], warning: 'No lots available for the given weight.' };
  }
  
  if (remainingTarget > 0.001) {
      return { 
        lots: newLots, 
        warning: `Insufficient stock. Allocated ${(targetWeight - remainingTarget).toFixed(3)}kg out of ${targetWeight}kg.` 
      };
  }

  return { lots: newLots };
};
